import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import logger from '../../../../utils/logger';

const SupportCapabilities = ({ agentId, showNotification }) => {
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchCapabilities();
  }, [agentId]);

  const fetchCapabilities = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/ai/agents/${agentId}/capabilities/support`);
      if (response.data.success) {
        setCapabilities(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching support capabilities:', error);
      showNotification?.('Failed to load support capabilities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.put(
        `/api/ai/agents/${agentId}/capabilities/support`,
        capabilities
      );
      if (response.data.success) {
        showNotification?.('Support capabilities saved successfully', 'success');
        setHasChanges(false);
      }
    } catch (error) {
      logger.error('Error saving support capabilities:', error);
      showNotification?.('Failed to save support capabilities', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    fetchCapabilities();
    setHasChanges(false);
  };

  const updateCapability = (path, value) => {
    setCapabilities(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updated;
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!capabilities) {
    return <div className="p-4 text-gray-500">No support capabilities configured</div>;
  }

  return (
    <div className="space-y-6">
      {/* Master Enable Toggle */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={capabilities.enabled || false}
            onChange={(e) => updateCapability('enabled', e.target.checked)}
            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="ml-3 font-medium text-gray-900 dark:text-white">
            Enable Support Capabilities
          </span>
        </label>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Enable this agent to handle customer support tasks including ticket routing, SLA management, customer history, and multichannel support.
        </p>
      </div>

      {capabilities.enabled && (
        <>
          {/* Ticket Routing */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Ticket Routing</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.ticketRouting?.enabled || false}
                  onChange={(e) => updateCapability('ticketRouting.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable ticket routing</span>
              </label>

              {capabilities.ticketRouting?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.ticketRouting?.autoRoute || false}
                      onChange={(e) => updateCapability('ticketRouting.autoRoute', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-route tickets</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.ticketRouting?.roundRobin || false}
                      onChange={(e) => updateCapability('ticketRouting.roundRobin', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Use round-robin assignment</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.ticketRouting?.skillBasedRouting || false}
                      onChange={(e) => updateCapability('ticketRouting.skillBasedRouting', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Enable skill-based routing</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* SLA Management */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">SLA Management</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.slaManagement?.enabled || false}
                  onChange={(e) => updateCapability('slaManagement.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable SLA management</span>
              </label>

              {capabilities.slaManagement?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.slaManagement?.autoEscalate || false}
                      onChange={(e) => updateCapability('slaManagement.autoEscalate', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-escalate SLA breaches</span>
                  </label>

                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Breach Alerts</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.slaManagement?.breachAlerts?.enabled || false}
                        onChange={(e) => updateCapability('slaManagement.breachAlerts.enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Enable breach alerts</span>
                    </label>

                    {capabilities.slaManagement?.breachAlerts?.enabled && (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={capabilities.slaManagement?.breachAlerts?.notifyManagers || false}
                          onChange={(e) => updateCapability('slaManagement.breachAlerts.notifyManagers', e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="ml-2 text-sm">Notify managers on breach</span>
                      </label>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Customer History */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Customer History</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.customerHistory?.enabled || false}
                  onChange={(e) => updateCapability('customerHistory.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable customer history</span>
              </label>

              {capabilities.customerHistory?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.customerHistory?.showPreviousTickets || false}
                      onChange={(e) => updateCapability('customerHistory.showPreviousTickets', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Show previous tickets</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.customerHistory?.sentimentTracking || false}
                      onChange={(e) => updateCapability('customerHistory.sentimentTracking', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Track customer sentiment</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.customerHistory?.satisfactionScores || false}
                      onChange={(e) => updateCapability('customerHistory.satisfactionScores', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Track satisfaction scores</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Knowledge Base */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Knowledge Base</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.knowledgeBase?.enabled || false}
                  onChange={(e) => updateCapability('knowledgeBase.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable knowledge base</span>
              </label>

              {capabilities.knowledgeBase?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.knowledgeBase?.autoSuggest || false}
                      onChange={(e) => updateCapability('knowledgeBase.autoSuggest', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-suggest articles</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.knowledgeBase?.selfServicePortal || false}
                      onChange={(e) => updateCapability('knowledgeBase.selfServicePortal', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Enable self-service portal</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.knowledgeBase?.articleFeedback || false}
                      onChange={(e) => updateCapability('knowledgeBase.articleFeedback', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Collect article feedback</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Multichannel Support */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Multichannel Support</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.multichannel?.email || false}
                  onChange={(e) => updateCapability('multichannel.email', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Email support</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.multichannel?.chat || false}
                  onChange={(e) => updateCapability('multichannel.chat', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Chat support</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.multichannel?.voice || false}
                  onChange={(e) => updateCapability('multichannel.voice', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Voice support</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.multichannel?.social || false}
                  onChange={(e) => updateCapability('multichannel.social', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Social media support</span>
              </label>
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-[#1a1a1a] p-4 border-t border-gray-200 dark:border-gray-800 -mx-6 -mb-6">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SupportCapabilities;
